import {
  type Account,
  type RestoreAccount,
  type GCSBucket,
  Provider,
  type S3Bucket,
} from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { getAccountProvider } from '@/utils/accounts';

const normalizeBuckets = (buckets: GCSBucket[] | S3Bucket[] | undefined) =>
  buckets?.map((bucket) => ({
    bucketName: bucket.bucketName,
    regionName: 'location' in bucket ? bucket.location : bucket.regionName,
  })) as S3Bucket[];

interface BucketsResult {
  buckets: S3Bucket[];
  isLoading: boolean;
  missingListPermissions: boolean;
}

export const useBuckets = (
  account?: Account | RestoreAccount,
  region?: string
): BucketsResult => {
  const dal = useDAL();
  const cloudProvider = getAccountProvider(account);
  const accountId = account?.id;
  const bucketsResponse = {
    [Provider.Aws]: dal.restore.s3Buckets.useList(accountId, cloudProvider),
    [Provider.Gcp]: dal.restore.gcsBuckets.useList(accountId, cloudProvider),
    [Provider.Azure]: null,
    [Provider.MongoAtlas]: null,
    [Provider.ProviderUnspecified]: null,
  }[cloudProvider ?? Provider.ProviderUnspecified];

  const bucketsData = bucketsResponse?.data?.buckets;
  const isLoading = bucketsResponse?.isLoading ?? false;
  const missingListPermissions = bucketsResponse?.error?.code === 403;

  return useMemo(() => {
    const originBuckets = normalizeBuckets(bucketsData) ?? [];

    return {
      isLoading,
      buckets: originBuckets.filter(
        (bucket) => !region || bucket.regionName === region
      ),
      missingListPermissions,
    };
  }, [bucketsData, isLoading, missingListPermissions, region]);
};
